// spell-casting-time.ts — the SINGLE source of truth for rendering a spell/ability's
// `spell_casting_time` layer across every client (cache-driven) render surface: the side-info
// object card (object-card), the spell list row + "Casting Time:" detail row (list-display), and
// the PC-sheet Mentas / Prepare-Spells tabs (player-character-sheet).
//
// This used to be hand-copied into each of those bundles and kept drifting out of parity (e.g. a
// `ten_minutes` ability silently lost its "10 Minutes" badge in the side panel). Centralizing it
// here means the next cast-time code added can't desync the TS surfaces.
//
// iconMap() is a runtime global (core-helpers.js, attached to window). Reach it through globalThis
// rather than the bare identifier so this module type-checks under EVERY consuming bundle's tsconfig
// — not all of them declare `iconMap` ambiently (matches the shared/render-table-tag.ts pattern).

import { layerOf } from './array-types';

const _g: any = globalThis as any;
function iconMapSafe(key: string): string {
    return typeof _g.iconMap === 'function' ? _g.iconMap(key) : '';
}

// Long cast-time code → full label (badge form). AP/reaction codes are NOT here — they render as a
// direct icon.
export const SPELL_CASTING_TIME_LABELS: Record<string, string> = {
    one_minute: '1 Minute',
    ten_minutes: '10 Minutes',
    one_hour: '1 Hour',
    eight_hours: '8 Hours',
    twelve_hours: '12 Hours',
    twenty_four_hours: '24 Hours',
};

// Long cast-time code → SHORT label (compact tables/cells).
export const SPELL_CASTING_TIME_SHORT_LABELS: Record<string, string> = {
    one_minute: '1 Min',
    ten_minutes: '10 Min',
    one_hour: '1 Hr',
    eight_hours: '8 Hrs',
    twelve_hours: '12 Hrs',
    twenty_four_hours: '24 Hrs',
};

// The short cast-time codes that render as a direct AP/reaction icon (no text label).
export const SPELL_CASTING_TIME_AP_CODES = ['1ap', '2ap', '3ap', 'reaction'] as const;

/** True for the AP/reaction cast-time codes (rendered as a flat icon, not a labelled badge). */
export function isApCastTime(ct: string): boolean {
    return (SPELL_CASTING_TIME_AP_CODES as readonly string[]).includes(ct);
}

/**
 * Full HTML for a cast time — AP/reaction → flat icon; a long code → the 3ap icon + a `bg-dark`
 * badge carrying the human label. Port of PHP display_spell_casting_time(). Used by the side-info
 * object card and the spell/curse list row.
 */
export function displaySpellCastingTime(arr: any): string {
    return displaySpellCastingTimeCode(String(layerOf<string>(arr, 'spell_casting_time') ?? ''));
}

/** Raw layer mounts in archived cards use the same labels and icons as full cards. */
export function displaySpellCastingTimeCode(ct: string): string {
    if (isApCastTime(ct)) return iconMapSafe(ct);
    const label = SPELL_CASTING_TIME_LABELS[ct];
    return label ? iconMapSafe('3ap') + " <span class='badge bg-dark border'>" + label + "</span>" : '';
}

/** Short text label only (no icon / markup) for a long cast-time code. Port of PHP get_spell_time_display(). */
export function getSpellTimeShortLabel(ct: string): string {
    return SPELL_CASTING_TIME_SHORT_LABELS[ct] ?? '';
}
